import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Loader2, ExternalLink } from 'lucide-react';
import { applicationService, Application } from '../services/applicationService';

export function ApplicationsManager() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApplications = async () => {
    setLoading(true);
    const data = await applicationService.getApplications();
    setApplications(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleApprove = async (id: number) => {
    const result = await applicationService.approveApplication(id);
    if (result.success) {
      fetchApplications();
    }
  };

  const handleReject = async (id: number) => {
    const result = await applicationService.rejectApplication(id);
    if (result.success) {
      fetchApplications();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Contributor Applications
      </h2>

      <div className="space-y-6">
        {applications.map((app) => (
          <motion.div
            key={app.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {app.full_name}
                </h3>
                <p className="text-gray-600">{app.email}</p>
              </div>
              <div className="flex space-x-2">
                {app.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(app.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleReject(app.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                )}
                <span
                  className={`px-3 py-1 rounded-full text-sm ${
                    app.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : app.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {app.status}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-700">Experience</h4>
                <p className="text-gray-600 mt-1">{app.experience}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700">Motivation</h4>
                <p className="text-gray-600 mt-1">{app.motivation}</p>
              </div>
            </div>

            <div className="mt-4 flex space-x-4">
              {app.github_url && (
                <a
                  href={app.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 flex items-center"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  GitHub Profile
                </a>
              )}
              {app.linkedin_url && (
                <a
                  href={app.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-700 flex items-center"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  LinkedIn Profile
                </a>
              )}
            </div>

            {app.status === 'approved' && app.admin_key && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800">Admin Key:</h4>
                <p className="font-mono text-sm mt-1">{app.admin_key}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
} 